// services/billing.service.js
import billingClient, { normalizeBillingError } from '../api/billingClient';

/**
 * Billing Service — read-only wrappers around the `billing_service`
 * REST API.
 *
 * Each method returns a uniform `{ success, status, data | error }`
 * shape, matching the existing service convention in this repo
 * (account.service.js, organization.service.js, etc.). Errors are
 * normalized through `normalizeBillingError` so callers can branch on
 * `error.code` (the canonical SCREAMING_SNAKE codes documented in
 * `notes/billing-service/03-error-format.md`).
 *
 * Admin endpoints under `/admin/*` are intentionally **not** exposed
 * here — those are platform-admin only and live in a separate
 * (eventual) ops console. See `notes/billing-user-stories.md §6`.
 *
 * NOTE on URL prefix: when the env var points at the API Gateway
 * invoke URL it ends in `/v1`; when it points at the custom domain
 * it does not. Routes here start with `/`.
 */
const BillingService = {
  // ---------------------------------------------------------------
  // Tiers (public catalog + per-account tier)
  // ---------------------------------------------------------------

  /**
   * GET /tiers — public tier catalog. No auth required, but the FE
   * always calls it from authenticated views.
   */
  async listTiers() {
    try {
      const response = await billingClient.get('/tiers');
      return {
        success: true,
        status: response.status,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        ...normalizeBillingError(error),
      };
    }
  },

  /**
   * GET /accounts/{accountId}/tier — current tier of one account.
   * Requires VIEWER+ on the org owning the account.
   */
  async getAccountTier(accountId) {
    try {
      const response = await billingClient.get(
        `/accounts/${accountId}/tier`
      );
      return {
        success: true,
        status: response.status,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        ...normalizeBillingError(error),
      };
    }
  },

  // ---------------------------------------------------------------
  // Account-scoped reads (VIEWER+ on the owning org)
  // ---------------------------------------------------------------

  /**
   * GET /accounts/{accountId}/billing/balance — period + extra balance
   * + tier summary + organization context (isEnterprisePool flag).
   */
  async getAccountBalance(accountId) {
    try {
      const response = await billingClient.get(
        `/accounts/${accountId}/billing/balance`
      );
      return {
        success: true,
        status: response.status,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        ...normalizeBillingError(error),
      };
    }
  },

  /**
   * GET /accounts/{accountId}/billing/transactions — cursor-paginated
   * ledger.
   *
   * @param {string} accountId
   * @param {Object} [params]
   * @param {string} [params.cursor]            opaque cursor
   * @param {number} [params.limit=20]
   * @param {string[]} [params.resourceTypes]   list of ResourceType values
   * @param {string} [params.transactionType]   one of DEDUCTION|RECHARGE|REFUND|BONUS
   * @param {string} [params.since]             ISO 8601
   * @param {string} [params.until]             ISO 8601
   */
  async listTransactions(accountId, params = {}) {
    try {
      const query = new URLSearchParams();
      if (params.cursor) query.set('cursor', params.cursor);
      if (params.limit != null) query.set('limit', String(params.limit));
      if (params.resourceTypes && params.resourceTypes.length > 0) {
        query.set('resourceType', params.resourceTypes.join(','));
      }
      if (params.transactionType) {
        query.set('transactionType', params.transactionType);
      }
      if (params.since) query.set('since', params.since);
      if (params.until) query.set('until', params.until);
      // Solo cuando está puesto. Ausente vale `false` en el backend, así que
      // mandar `"false"` no aporta nada — y el endpoint rechaza con 400
      // `INVALID_QUERY_PARAM` cualquier grafía que no reconozca, así que
      // menos superficie para equivocarse.
      if (params.excludeZeroAmount) query.set('excludeZeroAmount', 'true');

      const tail = query.toString() ? `?${query.toString()}` : '';
      const response = await billingClient.get(
        `/accounts/${accountId}/billing/transactions${tail}`
      );
      return {
        success: true,
        status: response.status,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        ...normalizeBillingError(error),
      };
    }
  },

  /**
   * GET /accounts/{accountId}/billing/usage-summary — totals + per
   * resource breakdown for a window. Defaults to current period when
   * since/until are omitted.
   */
  async getUsageSummary(accountId, { since, until } = {}) {
    try {
      const query = new URLSearchParams();
      if (since) query.set('since', since);
      if (until) query.set('until', until);
      const tail = query.toString() ? `?${query.toString()}` : '';
      const response = await billingClient.get(
        `/accounts/${accountId}/billing/usage-summary${tail}`
      );
      return {
        success: true,
        status: response.status,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        ...normalizeBillingError(error),
      };
    }
  },

  // ---------------------------------------------------------------
  // Organization-scoped reads (ADMIN+, only when the org has an
  // Enterprise subscription — otherwise returns 404
  // NOT_AN_ENTERPRISE_ORG).
  // ---------------------------------------------------------------

  /**
   * GET /organizations/{orgId}/billing/balance — pool balance for the
   * Enterprise plan: period + extra + customMonthlyCoinAllowance.
   */
  async getOrgBalance(organizationId) {
    try {
      const response = await billingClient.get(
        `/organizations/${organizationId}/billing/balance`
      );
      return {
        success: true,
        status: response.status,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        ...normalizeBillingError(error),
      };
    }
  },

  /**
   * GET /organizations/{orgId}/subscription — full Enterprise sub row
   * (allowance, anchor date, last reset, created/updated timestamps).
   */
  async getOrgSubscription(organizationId) {
    try {
      const response = await billingClient.get(
        `/organizations/${organizationId}/subscription`
      );
      return {
        success: true,
        status: response.status,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        ...normalizeBillingError(error),
      };
    }
  },

  /**
   * GET /organizations/{orgId}/billing/usage-by-account — per-account
   * breakdown for the org pool, optionally narrowed by since/until.
   */
  async getOrgUsageByAccount(organizationId, { since, until } = {}) {
    try {
      const query = new URLSearchParams();
      if (since) query.set('since', since);
      if (until) query.set('until', until);
      const tail = query.toString() ? `?${query.toString()}` : '';
      const response = await billingClient.get(
        `/organizations/${organizationId}/billing/usage-by-account${tail}`
      );
      return {
        success: true,
        status: response.status,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        ...normalizeBillingError(error),
      };
    }
  },

  // ---------------------------------------------------------------
  // Pricing catalog + use-case calculator (public, no auth)
  // ---------------------------------------------------------------

  /**
   * GET /pricing/catalog — official price list per resource +
   * assistant tiers grouped by cost_tier + enabled image models +
   * coming-soon features (currently RAG).
   *
   * Public endpoint; the FE typically calls it once per session.
   */
  async getPricingCatalog() {
    try {
      const response = await billingClient.get('/pricing/catalog');
      return {
        success: true,
        status: response.status,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        ...normalizeBillingError(error),
      };
    }
  },

  /**
   * GET /pricing/use-cases — catalog of pre-built calculator templates
   * (e.g. "chatbot") with their configurable knobs and PyME-sized
   * example presets.
   */
  async listPricingUseCases() {
    try {
      const response = await billingClient.get('/pricing/use-cases');
      return {
        success: true,
        status: response.status,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        ...normalizeBillingError(error),
      };
    }
  },

  /**
   * POST /pricing/estimate — calculator. Accepts either mode A
   * (template-driven) or mode B (custom items), never both.
   *
   * @param {Object} request
   * @param {string} [request.useCaseId] — mode A: template id (e.g. "chatbot")
   * @param {Object} [request.knobs]     — mode A: knob bag (e.g. {conversationsPerMonth: 300})
   * @param {Array<{resource: string, quantity: number, tier?: number, modelKey?: string}>} [request.items]
   *                                     — mode B: custom line items
   */
  async estimateUsageCost(request) {
    try {
      const response = await billingClient.post('/pricing/estimate', request);
      return {
        success: true,
        status: response.status,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        ...normalizeBillingError(error),
      };
    }
  },

  /**
   * GET /organizations/{orgId}/quotas — list of quota policies
   * (read-only; the upsert endpoint is not wired in backend today).
   */
  async listOrgQuotas(organizationId) {
    try {
      const response = await billingClient.get(
        `/organizations/${organizationId}/quotas`
      );
      return {
        success: true,
        status: response.status,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        ...normalizeBillingError(error),
      };
    }
  },

  // ---------------------------------------------------------------
  // Payments (Tilopay) — coin packages + subscription checkout
  // ---------------------------------------------------------------

  /**
   * GET /payments/packages — public catalog of purchasable coin
   * packages.
   */
  async listPackages() {
    try {
      const response = await billingClient.get('/payments/packages');
      return {
        success: true,
        status: response.status,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        ...normalizeBillingError(error),
      };
    }
  },

  /**
   * POST /accounts/{accountId}/payments/checkout-session — starts a
   * coin-package checkout. Hosted mode (the default) returns
   * `checkoutUrl` (a Tilopay-hosted page — redirect the browser
   * there); embedded mode (`checkoutMode: 'embedded'`) returns
   * `extra.sdk_token` for the Tilopay SDK's own form instead.
   *
   * @param {string} accountId
   * @param {Object} request
   * @param {string} request.packageId
   * @param {string} [request.redirectUrl] where Tilopay sends the
   *        browser back to after payment (hosted mode)
   * @param {string} [request.checkoutMode='hosted'] 'hosted' | 'embedded'
   * @param {string} [request.paymentMethod='card']
   */
  async createCheckoutSession(
    accountId,
    { packageId, redirectUrl, checkoutMode = 'hosted', paymentMethod = 'card' }
  ) {
    try {
      const response = await billingClient.post(
        `/accounts/${accountId}/payments/checkout-session`,
        {
          packageId,
          provider: 'tilopay',
          checkoutMode,
          paymentMethod,
          ...(redirectUrl ? { redirectUrl } : {}),
        }
      );
      return {
        success: true,
        status: response.status,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        ...normalizeBillingError(error),
      };
    }
  },

  /**
   * POST /accounts/{accountId}/payments/intents/{orderNumber}/confirm
   * — best-effort immediate confirmation after the Tilopay redirect
   * returns. The backend cron reconciles within ~15 min regardless, so
   * a failure here is not fatal to the purchase.
   */
  async confirmIntent(accountId, orderNumber) {
    try {
      const response = await billingClient.post(
        `/accounts/${accountId}/payments/intents/${orderNumber}/confirm`
      );
      return {
        success: true,
        status: response.status,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        ...normalizeBillingError(error),
      };
    }
  },

  /**
   * POST /accounts/{accountId}/payments/subscription/checkout —
   * starts a Tilopay-hosted checkout for a subscription tier. Never
   * send the user's email — the backend anchors it from the
   * authenticated JWT (security note B1).
   *
   * @param {string} accountId
   * @param {string} tierType e.g. 'STARTER' | 'PRO' | 'ENTERPRISE'
   */
  async createSubscriptionCheckout(accountId, tierType) {
    try {
      const response = await billingClient.post(
        `/accounts/${accountId}/payments/subscription/checkout`,
        { tierType, provider: 'tilopay' }
      );
      return {
        success: true,
        status: response.status,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        ...normalizeBillingError(error),
      };
    }
  },

  /**
   * GET /accounts/{accountId}/payments/subscription — current
   * subscription record for the account (may be `pending` if a
   * checkout was started but never completed; the backend does not
   * expire these automatically, so the caller is responsible for
   * canceling stale ones — see `cancelSubscription`).
   *
   * @param {string} accountId
   */
  async getSubscriptionStatus(accountId) {
    try {
      const response = await billingClient.get(
        `/accounts/${accountId}/payments/subscription`
      );
      return {
        success: true,
        status: response.status,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        ...normalizeBillingError(error),
      };
    }
  },

  /**
   * POST /accounts/{accountId}/payments/subscription/cancel — cancels
   * the account's current subscription record. Used to clear an
   * abandoned `pending` checkout (the backend blocks new checkouts
   * with 409 DUPLICATE_SUBSCRIPTION while one exists) before starting
   * a fresh one.
   *
   * @param {string} accountId
   */
  async cancelSubscription(accountId) {
    try {
      const response = await billingClient.post(
        `/accounts/${accountId}/payments/subscription/cancel`
      );
      return {
        success: true,
        status: response.status,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        ...normalizeBillingError(error),
      };
    }
  },
};

export default BillingService;
